(() => {
  'use strict';
  if (document.body?.classList.contains('fqfoundation-document')) {
    const chatSelector = 'shopify-chat, #shopify-chat, .fqtl8-help';
    const removeChat = node => {
      if (node.nodeType !== 1) return;
      if (node.matches(chatSelector)) { node.remove(); return; }
      node.querySelectorAll(chatSelector).forEach(chat => chat.remove());
    };
    removeChat(document.body);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(removeChat)))
      .observe(document.body, { childList: true, subtree: true });
  }

  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const wrap = (value, length) => (value + length) % length;

  function mount(root) {
    if (instances.has(root)) return;
    const tabs = [...root.querySelectorAll('[data-foundation-tab]')];
    const panels = [...root.querySelectorAll('[data-foundation-panel]')];
    if (!tabs.length || tabs.length !== panels.length) return;
    const controller = new AbortController(), { signal } = controller;
    const rail = root.querySelector('[data-foundation-timeline]');
    const announce = root.querySelector('[data-foundation-announcement]');
    const motion = root.querySelector('[data-foundation-motion]');
    const reading = root.querySelector('[data-foundation-reading]');
    const hero = root.querySelector('[data-foundation-hero]');
    const nav = root.querySelector('.fqfoundation__nav');
    const historyScene = root.querySelector('[data-foundation-scroll-scene]');
    const historyStage = historyScene?.querySelector('[data-foundation-scroll-stage]');
    const historyMarker = historyStage?.querySelector('[data-foundation-history-marker]');
    const currentYears = [...root.querySelectorAll('[data-foundation-current-year]')];
    const yearFormat = new Intl.DateTimeFormat('en', { timeZone: 'Europe/Paris', year: 'numeric' });
    const progress = root.querySelector('[data-foundation-progress]');
    const preferences = document.querySelector('[data-foundation-preferences]');
    const endLine = root.querySelector('.fqfoundation__footer small');
    const canvas = root.querySelector('[data-foundation-sky]');
    const ctx = canvas?.getContext('2d');
    const media = matchMedia('(prefers-reduced-motion: reduce)');
    const fine = matchMedia('(hover: hover) and (pointer: fine)');
    const titleWords = new Map(), styles = new WeakMap();
    let active = Math.max(0, tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true'));
    let motionOverride = null, readMode = false, paused = true;
    let frame = 0, skyFrame = 0, last = 0, phase = 0, width = 0, height = 0;
    let px = 0, py = 0, pulses = [], heroVisible = true, scrolling = false, scrollIdle = 0;
    let observer, visibility, sizing, yearTimer = 0;
    try {
      if (sessionStorage.getItem('fqfoundation-motion') === 'paused') motionOverride = true;
      readMode = sessionStorage.getItem('fqfoundation-reading') === 'true';
    } catch (_) {}

    // Keep section anchors on this exact page, even when global routing rewrites
    // fragment-only links to the homepage. Capture them before theme navigation.
    const anchorTargets = {
      history: root.querySelector('.fqfoundation__history'),
      board: root.querySelector('.fqfoundation__board')
    };
    const localAnchors = new Map();
    function refreshAnchor(link, target) {
      const destination = new URL(window.location.href);
      destination.hash = target.id;
      link.href = destination.href;
      return destination;
    }
    root.querySelectorAll('a[data-foundation-scroll-to], a[href*="#FQFoundation-History-"], a[href*="#FQFoundation-Board-"]').forEach(link => {
      const key = link.dataset.foundationScrollTo || (link.getAttribute('href').includes('#FQFoundation-History-') ? 'history' : 'board');
      const target = anchorTargets[key];
      if (!target?.id) return;
      localAnchors.set(link, target);
      refreshAnchor(link, target);
    });
    window.addEventListener('click', event => {
      const link = event.target?.closest?.('a');
      const target = localAnchors.get(link);
      if (!target) return;
      const destination = refreshAnchor(link, target);
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        if (window.location.hash !== destination.hash) history.pushState(history.state, '', destination.href);
      } catch (_) {}
      const top = target.getBoundingClientRect().top + window.scrollY - (nav?.offsetHeight || 88) - 16;
      window.scrollTo({ top: Math.max(0, top), behavior: paused ? 'auto' : 'smooth' });
      if (event.detail === 0) {
        const heading = target.querySelector('h2') || target;
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }
      schedule();
    }, { capture: true, signal });

    // Preserve the original line breaks and silver spans while revealing words.
    root.querySelectorAll('[data-foundation-text-reveal]').forEach(title => {
      const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      const label = title.innerText.replace(/\s+/g, ' ').trim();
      const words = [];
      title.setAttribute('aria-label', label);
      nodes.forEach(node => {
        const fragment = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(word => {
          if (!word || /^\s+$/.test(word)) { fragment.append(document.createTextNode(word)); return; }
          const span = document.createElement('span');
          span.className = 'fqfoundation__reveal-word';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = word;
          fragment.append(span); words.push(span);
        });
        node.replaceWith(fragment);
      });
      title.style.setProperty('--word-count', String(words.length));
      words.forEach((word, index) => word.style.setProperty('--word-index', String(index)));
      titleWords.set(title, words);
    });

    function setStyle(node, name, value) {
      if (!node) return;
      let values = styles.get(node);
      if (!values) { values = new Map(); styles.set(node, values); }
      if (values.get(name) === value) return;
      values.set(name, value); node.style.setProperty(name, value);
    }
    function updateYear() {
      clearTimeout(yearTimer);
      const year = yearFormat.format(new Date());
      currentYears.forEach(node => { if (node.textContent !== year) node.textContent = year; });
      // Align checks to the minute so midnight in Paris updates automatically.
      if (!document.hidden && !signal.aborted) yearTimer = setTimeout(updateYear, 60000 - Date.now() % 60000 + 20);
    }
    function sizeHistoryScene() {
      const navHeight = nav?.offsetHeight || 88;
      const fits = historyStage && innerHeight - navHeight >= historyStage.offsetHeight;
      setStyle(root, '--fq-foundation-nav-height', `${navHeight}px`);
      // Short viewports, reading mode and reduced motion use the natural layout.
      root.dataset.pinnedHistory = String(Boolean(historyScene && fits && !paused));
      positionHistoryMarker();
    }
    function positionHistoryMarker() {
      if (!historyMarker || !historyStage || !rail) return;
      const dot = tabs[active].querySelector('.fqfoundation__dot');
      if (!dot) return;
      const stageBounds = historyStage.getBoundingClientRect();
      const dotBounds = dot.getBoundingClientRect();
      const panelBounds = panels[active].getBoundingClientRect();
      const railBounds = rail.getBoundingClientRect();
      const center = dotBounds.left + dotBounds.width / 2;
      historyMarker.hidden = readMode || center < railBounds.left || center > railBounds.right;
      setStyle(historyMarker, 'left', `${center - stageBounds.left}px`);
      setStyle(historyMarker, 'top', `${dotBounds.bottom - stageBounds.top}px`);
      setStyle(historyMarker, 'height', `${Math.max(0, panelBounds.top + Math.min(76, panelBounds.height * .17) - dotBounds.bottom)}px`);
    }
    rail?.addEventListener('scroll', positionHistoryMarker, { passive: true, signal });
    function alignTab(tab) {
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      const left = tab.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft - (rail.clientWidth - tab.offsetWidth) / 2;
      rail.scrollTo({ left: Math.max(0, left), behavior: paused ? 'auto' : 'smooth' });
    }
    function select(index, focus = false, announceChange = true, align = true) {
      active = wrap(index, tabs.length);
      tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === active));
        tab.tabIndex = i === active ? 0 : -1;
        panels[i].hidden = i !== active;
      });
      sizeHistoryScene();
      if (focus) tabs[active].focus({ preventScroll: true });
      if (align) alignTab(tabs[active]);
      if (announceChange && announce) {
        const values = {
          index: active + 1, total: tabs.length,
          date: tabs[active].querySelector('.fqfoundation__date').textContent.trim(),
          title: panels[active].querySelector('h3').getAttribute('aria-label')
        };
        announce.textContent = root.dataset.announcement.replace(/\{(index|total|date|title)\}/g, (_, key) => values[key]);
      }
      schedule();
    }
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index), { signal });
      tab.addEventListener('keydown', event => {
        const target = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: tabs.length - 1 }[event.key];
        if (target === undefined) return;
        event.preventDefault(); select(target, true);
      }, { signal });
    });
    root.querySelector('[data-foundation-prev]')?.addEventListener('click', () => select(active - 1), { signal });
    root.querySelector('[data-foundation-next]')?.addEventListener('click', () => select(active + 1), { signal });
    root.querySelectorAll('[data-foundation-jump]').forEach(button => {
      button.hidden = false;
      button.addEventListener('click', () => {
        select(Number(button.dataset.foundationJump));
        panels[active].focus({ preventScroll: true });
      }, { signal });
    });
    root.querySelector('[data-foundation-controls]').hidden = false;

    function resizeSky() {
      if (!canvas || !ctx) return;
      width = canvas.clientWidth; height = canvas.clientHeight;
      const ratio = Math.min(devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * ratio); canvas.height = Math.round(height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }
    // The same nine light trails, twenty-three stars and touch ripples as Career.
    function drawSky() {
      if (!ctx || !width || !height) return;
      ctx.clearRect(0, 0, width, height);
      if (paused) return;
      for (let i = 0; i < 9; i++) {
        const y = height * (.69 + i * .014), lift = Math.sin(phase * .19 + i * .23) * 12;
        ctx.beginPath(); ctx.moveTo(-width * .1, y + 40);
        ctx.bezierCurveTo(width * .3, y - 30 + lift + py * 15, width * .64, y + 55 - lift, width * 1.1, y - 38);
        ctx.lineWidth = .55; ctx.strokeStyle = `rgba(171,201,249,${.014 + i * .002})`; ctx.stroke();
      }
      for (let i = 0; i < 23; i++) {
        const x = width * ((i * .618033) % 1) + Math.sin(phase * .15 + i) * 7;
        const y = height * (.16 + ((i * .373) % 1) * .53) + Math.cos(phase * .12 + i) * 5;
        const alpha = (.08 + .06 * Math.sin(phase * .7 + i)) * Math.max(.2, 1 - Math.abs(x / width - (px + .5)));
        ctx.fillStyle = `rgba(184,211,255,${alpha})`; ctx.beginPath(); ctx.arc(x, y, .6 + (i % 3) * .2, 0, Math.PI * 2); ctx.fill();
      }
      pulses = pulses.filter(pulse => phase - pulse.t < 2.5);
      pulses.forEach(pulse => {
        const age = phase - pulse.t;
        ctx.beginPath(); ctx.arc(pulse.x, pulse.y, age * 65 + 8, 0, Math.PI * 2);
        ctx.lineWidth = .7; ctx.strokeStyle = `rgba(183,216,255,${Math.max(0, .19 - age * .076)})`; ctx.stroke();
      });
    }
    function animate(ts) {
      skyFrame = 0;
      if (signal.aborted || paused || !heroVisible || document.hidden || scrolling) return;
      if (last && ts - last < 32) { skyFrame = requestAnimationFrame(animate); return; }
      phase += last ? Math.min(ts - last, 80) / 1000 : 0;
      last = ts; drawSky(); skyFrame = requestAnimationFrame(animate);
    }
    function skySchedule() {
      if (skyFrame) cancelAnimationFrame(skyFrame);
      skyFrame = 0; last = 0;
      if (!paused && heroVisible && !document.hidden && !scrolling && ctx) skyFrame = requestAnimationFrame(animate);
      else drawSky();
    }
    function paint() {
      frame = 0;
      if (!root.isConnected || document.hidden) return;
      const viewport = innerHeight, bounds = root.getBoundingClientRect(), heroBounds = hero.getBoundingClientRect();
      const length = Math.max(1, root.offsetHeight - viewport), percent = clamp(-bounds.top / length);
      const shift = paused ? 0 : clamp(-heroBounds.top * .09, 0, 60);
      const end = endLine?.getBoundingClientRect();
      const lift = end && end.top < viewport && end.bottom > 0 ? Math.max(innerWidth <= 600 ? 10 : 18, viewport - end.top + 16) : innerWidth <= 600 ? 10 : 18;
      const updates = [], sceneBounds = historyScene?.getBoundingClientRect();
      const remaining = Math.max(0, document.documentElement.scrollHeight - viewport - scrollY);
      const atEnd = remaining <= 2;
      titleWords.forEach((words, title) => {
        if (title.closest('[hidden]')) return;
        const b = title.getBoundingClientRect();
        let ratio = paused ? 1 : clamp((viewport * .93 - b.top) / Math.max(160, viewport * .48));
        if (!paused && root.dataset.pinnedHistory === 'true' && title.dataset.foundationTextReveal === 'vision' && sceneBounds) {
          ratio = clamp((viewport * .9 - sceneBounds.top) / Math.max(1, sceneBounds.height * .85));
        }
        // Closing text must finish before the visitor runs out of page to scroll.
        if (!paused && title.closest('.fqfoundation__footer')) ratio = Math.max(ratio, clamp(1 - remaining / Math.max(160, viewport * .25)));
        if (atEnd) ratio = 1;
        updates.push({ title, words, ratio: Math.round(ratio * 10000) / 10000 });
      });
      setStyle(progress, 'transform', `scaleX(${Math.round(percent * 10000) / 10000})`);
      setStyle(root, '--scene-y', `${Math.round(shift * 100) / 100}px`);
      setStyle(preferences, '--fq-foundation-picker-lift', `${Math.round(lift)}px`);
      updates.forEach(({ title, words, ratio }) => {
        setStyle(title, '--title-progress', String(ratio));
        words.forEach((word, i) => {
          const lit = paused || clamp((ratio * (words.length + 1.7) - i) / 1.7) >= .995;
          if (word.classList.contains('is-lit') !== lit) word.classList.toggle('is-lit', lit);
        });
      });
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(paint); }
    function applyMode(persist = false) {
      paused = readMode || root.dataset.motionEnabled === 'false' || (motionOverride === null ? media.matches : motionOverride);
      root.classList.toggle('is-paused', paused);
      root.dataset.reading = String(readMode); root.dataset.effects = String(!paused);
      reading?.setAttribute('aria-pressed', String(readMode));
      if (motion) {
        motion.setAttribute('aria-pressed', String(paused));
        motion.querySelector('[data-foundation-motion-label]').textContent = paused ? root.dataset.motionPaused : root.dataset.motionActive;
        motion.querySelector('[data-foundation-motion-icon]').textContent = paused ? '▷' : 'Ⅱ';
      }
      if (paused) {
        px = py = 0; setStyle(hero, '--px', '0'); setStyle(hero, '--py', '0');
        root.querySelectorAll('.is-pending').forEach(el => el.classList.remove('is-pending'));
      }
      if (persist) {
        try {
          sessionStorage.setItem('fqfoundation-motion', motionOverride ? 'paused' : 'active');
          sessionStorage.setItem('fqfoundation-reading', String(readMode));
        } catch (_) {}
      }
      sizeHistoryScene(); resizeSky(); skySchedule(); schedule();
    }
    if (motion) {
      motion.hidden = root.dataset.motionEnabled === 'false';
      motion.addEventListener('click', () => { readMode = false; motionOverride = !paused; applyMode(true); }, { signal });
    }
    if (reading) {
      reading.hidden = false;
      reading.addEventListener('click', () => { readMode = !readMode; applyMode(true); }, { signal });
    }
    const onMediaChange = () => { motionOverride = null; applyMode(); };
    if (media.addEventListener) media.addEventListener('change', onMediaChange, { signal });
    else media.addListener(onMediaChange);
    window.addEventListener('scroll', () => {
      if (!scrolling) { scrolling = true; skySchedule(); }
      clearTimeout(scrollIdle);
      scrollIdle = setTimeout(() => { scrolling = false; skySchedule(); }, 140);
      schedule();
    }, { passive: true, signal });
    window.addEventListener('resize', () => { sizeHistoryScene(); resizeSky(); schedule(); skySchedule(); }, { passive: true, signal });
    document.addEventListener('visibilitychange', () => { updateYear(); skySchedule(); schedule(); }, { signal });
    window.addEventListener('pageshow', () => { updateYear(); sizeHistoryScene(); schedule(); }, { signal });
    hero.addEventListener('pointermove', event => {
      if (paused || !fine.matches || event.pointerType !== 'mouse') return;
      const box = hero.getBoundingClientRect();
      px = (event.clientX - box.left) / box.width - .5; py = (event.clientY - box.top) / box.height - .5;
      setStyle(hero, '--px', String(px)); setStyle(hero, '--py', String(py));
      setStyle(hero, '--light-x', `${(px + .5) * 100}%`); setStyle(hero, '--light-y', `${(py + .5) * 100}%`);
    }, { passive: true, signal });
    hero.addEventListener('pointerleave', () => { px = py = 0; setStyle(hero, '--px', '0'); setStyle(hero, '--py', '0'); }, { signal });
    hero.addEventListener('pointerdown', event => {
      if (paused || event.target.closest('a,button')) return;
      const box = hero.getBoundingClientRect();
      pulses.push({ x: event.clientX - box.left, y: event.clientY - box.top, t: phase }); pulses = pulses.slice(-4);
    }, { signal });
    root.classList.add('is-enhanced');
    document.documentElement.classList.replace('no-js', 'js');
    select(active, false, false, false); applyMode();
    updateYear();
    if ('ResizeObserver' in window) {
      sizing = new ResizeObserver(() => { sizeHistoryScene(); schedule(); });
      if (nav) sizing.observe(nav);
      if (historyStage) sizing.observe(historyStage);
    }
    if ('IntersectionObserver' in window) {
      visibility = new IntersectionObserver(entries => { heroVisible = entries[0].isIntersecting; skySchedule(); }, { threshold: 0 });
      visibility.observe(hero);
      if (!paused) {
        observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('is-pending'); observer.unobserve(entry.target);
        }), { threshold: .025 });
        root.querySelectorAll('[data-foundation-reveal]').forEach(el => { el.classList.add('is-pending'); observer.observe(el); });
      }
    }
    instances.set(root, { destroy() {
      controller.abort(); observer?.disconnect(); visibility?.disconnect(); sizing?.disconnect(); clearTimeout(scrollIdle); clearTimeout(yearTimer);
      if (!media.removeEventListener) media.removeListener(onMediaChange);
      if (frame) cancelAnimationFrame(frame); if (skyFrame) cancelAnimationFrame(skyFrame);
      instances.delete(root);
    } });
  }
  function scan(container = document) {
    if (container.matches?.('[data-fq-foundation]')) mount(container);
    container.querySelectorAll('[data-fq-foundation]').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.('[data-fq-foundation]')) instances.get(event.target)?.destroy();
    event.target.querySelectorAll('[data-fq-foundation]').forEach(root => instances.get(root)?.destroy());
  });
})();
